// 认证相关API服务 - 纯Cookie认证版本
import { apiPost, apiGet, ApiError } from '@/lib/api';
import { extractInternalPath } from '@/lib/permission-access';

// 登录请求参数 - 根据swagger定义
export interface LoginRequest {
  account: string;
  password: string;
  source: 'browser' | 'plugin'; // 登录来源
  session_id?: string; // 会话ID，插件登录时必填
}

// 前端登录表单参数（包含rememberMe）
export interface LoginFormData {
  username: string;
  password: string;
  rememberMe?: boolean;
}

// 用户信息类型 - 根据swagger User定义
export interface UserInfo {
  id: string;
  username: string;
  account?: string; // 管理员账号
  email: string;
  phone?: string;
  avatar_url?: string;
  status: 'active' | 'inactive' | 'locked';
  created_at: number;
  last_active_at: number;
  is_deleted: boolean;
}

// 登录响应数据 - 根据swagger LoginResp定义
export interface LoginResponse {
  user: UserInfo;
  redirect_url?: string;
}

// 用户权限信息类型
export interface UserPermissions {
  menu_permissions: string[];
  data_scope: string;
  is_admin: boolean;
  business_role_id?: string;
  business_role?: string;
}

// 登录 - 前台用户登录使用username字段
export const login = async (
  credentials: LoginFormData
): Promise<LoginResponse> => {
  // 前台登录使用username字段
  const loginData = {
    username: credentials.username,
    password: credentials.password,
    source: 'browser', // 默认浏览器登录
  };

  try {
    const response = await apiPost<LoginResponse>(
      '/v1/user/login',
      loginData as unknown as Record<string, unknown>
    );

    return response;
  } catch (error) {
    // 登录失败时直接抛出错误，不使用mock数据
    console.error('🔐 登录失败:', error);
    throw error;
  }
};

// 管理员登录 - 管理后台登录使用account字段
export const adminLogin = async (
  credentials: LoginFormData
): Promise<LoginResponse> => {
  // 管理后台登录使用account字段
  const loginData: LoginRequest = {
    account: credentials.username,
    password: credentials.password,
    source: 'browser', // 默认浏览器登录
  };

  try {
    const url = '/api/v1/admin/login';
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(loginData),
    });

    if (!response.ok) {
      // 解析后端返回的错误信息
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.message || `HTTP ${response.status}: ${response.statusText}`;
      throw new ApiError(response.status, errorMessage, errorData);
    }

    // 尝试解析响应，处理可能的格式
    const responseData = await response.json();
    console.log('🔐 管理员登录原始响应:', responseData);
    console.log('🔐 响应类型:', typeof responseData);
    console.log('🔐 响应keys:', Object.keys(responseData || {}));

    // 处理可能的各种响应格式
    let result: LoginResponse;

    // 格式1: { data: { user: ... } }
    if (responseData?.data?.user) {
      console.log('🔐 检测到格式1: { data: { user: ... } }');
      result = responseData.data;
    }
    // 格式2: { user: ... }
    else if (responseData?.user) {
      console.log('🔐 检测到格式2: { user: ... }');
      result = responseData;
    }
    // 格式3: 响应本身就是 UserInfo 对象
    else if (responseData?.id && responseData?.username) {
      console.log('🔐 检测到格式3: 响应本身是UserInfo对象');
      result = { user: responseData };
    }
    // 格式4: 返回空对象或其他格式 - 生成一个基础用户对象
    else {
      console.log('🔐 检测到格式4或未知格式，尝试从响应构造用户对象');
      // 生成基础用户信息（从登录请求推断）
      result = {
        user: {
          id: 'admin_' + Date.now(),
          username: credentials.username,
          email: credentials.username + '@admin.local',
          status: 'active' as const,
          created_at: Math.floor(Date.now() / 1000),
          last_active_at: Math.floor(Date.now() / 1000),
          is_deleted: false,
        },
      };
    }

    console.log('🔐 最终处理后的响应:', result);
    return result;
  } catch (error) {
    // 登录失败时直接抛出错误，不使用mock数据
    console.error('🔐 管理员登录失败:', error);
    throw error;
  }
};

// 退出登录
export const logout = async (): Promise<void> => {
  try {
    // 调用后端退出登录接口，���务器会清除 session/cookie
    await apiPost('/v1/user/logout');
  } catch (error) {
    console.warn('🔐 后端登出失败:', error);
  }
};

// 管理员退出登录 - 专用于管理后台
export const adminLogout = async (): Promise<void> => {
  try {
    const url = '/api/v1/admin/logout';
    console.log('🔐 调用管理员登出接口:', url);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      console.warn('🔐 管理员后端登出返回错误状态:', response.status);
    } else {
      console.log('🔐 管理员后端登出成功');
    }
  } catch (error) {
    console.warn('🔐 管理员后端登出失败:', error);
  }
};

// 获取当前用户信息
export const getCurrentUser = async (): Promise<UserInfo> => {
  try {
    const response = await apiGet<UserInfo>('/v1/user/profile');
    return response;
  } catch (error) {
    console.error('🔐 获取用户信息失败:', error);
    throw error;
  }
};

// 获取当前管理员用户信息 - 专用于管理后台
export const getCurrentAdminUser = async (): Promise<UserInfo> => {
  try {
    const url = '/api/v1/admin/profile';
    console.log('🔐 调用管理员认证检查接口:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('管理员未登录或登录已过期');
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const responseData = await response.json();
    console.log('🔐 管理员认证检查响应:', responseData);
    console.log('🔐 响应类型:', typeof responseData);
    console.log('🔐 响应keys:', Object.keys(responseData || {}));

    // 根据后端 web.Resp{data=domain.AdminUser} 格式解析响应
    let userInfo: UserInfo;

    // 格式1: { data: { AdminUser对象 } }
    if (
      responseData?.data &&
      typeof responseData.data === 'object' &&
      responseData.data.id
    ) {
      console.log('🔐 检测到格式1: { data: AdminUser }');
      userInfo = responseData.data;
    }
    // 格式2: { data: { user: AdminUser对象 } }
    else if (
      responseData?.data?.user &&
      typeof responseData.data.user === 'object' &&
      responseData.data.user.id
    ) {
      console.log('🔐 检测到格式2: { data: { user: AdminUser } }');
      userInfo = responseData.data.user;
    }
    // 格式3: { user: AdminUser对象 }
    else if (
      responseData?.user &&
      typeof responseData.user === 'object' &&
      responseData.user.id
    ) {
      console.log('🔐 检测到格式3: { user: AdminUser }');
      userInfo = responseData.user;
    }
    // 格式4: 响应本身就是AdminUser对象
    else if (responseData?.id && responseData?.username) {
      console.log('🔐 检测到格式4: 响应本身是AdminUser对象');
      userInfo = responseData;
    } else {
      console.error('🔐 无法解析的响应格式:', responseData);
      throw new Error('管理员用户信息响应格式异常，无法找到有效用户数据');
    }

    // 确保必要字段存在，并映射到UserInfo接口
    const mappedUserInfo: UserInfo = {
      id: userInfo.id,
      username: userInfo.username,
      email: userInfo.email || `${userInfo.username}@admin.local`,
      account: userInfo.account || userInfo.username,
      phone: userInfo.phone,
      status: userInfo.status || 'active',
      created_at: userInfo.created_at || Math.floor(Date.now() / 1000),
      last_active_at: userInfo.last_active_at || Math.floor(Date.now() / 1000),
      is_deleted: userInfo.is_deleted || false,
      avatar_url: userInfo.avatar_url,
    };

    console.log('🔐 解析后的管理员用户信息:', mappedUserInfo);
    return mappedUserInfo;
  } catch (error) {
    console.error('🔐 获取管理员用户信息失败:', error);
    throw error;
  }
};

// OAuth 认证地址响应
export interface OAuthUrlResponse {
  url: string;
}

// OAuth 提供商信息
export interface OAuthProvider {
  platform: 'dingtalk' | 'custom' | 'baizhi';
  display_name: string;
  enabled: boolean;
}

// OAuth 方式列表响应
export interface ListOAuthProvidersResp {
  providers: OAuthProvider[];
}

// 获取 OAuth 方式列表
export const getOAuthProviders = async (): Promise<ListOAuthProvidersResp> => {
  try {
    const response = await apiGet<ListOAuthProvidersResp>(
      '/v1/user/oauth/providers'
    );
    return response;
  } catch (error) {
    console.error('🔐 获取OAuth方式列表失败:', error);
    throw error;
  }
};

// 获取OAuth认证地址
export const getOAuthUrl = async (
  platform: string,
  redirectPath: string = '/'
): Promise<string> => {
  const safeRedirectPath =
    extractInternalPath(redirectPath, window.location.origin) || '/';
  const redirectUrl = `${window.location.origin}${safeRedirectPath}`;

  try {
    // 传递回调地址给后端
    const response = await apiGet<OAuthUrlResponse>(
      '/v1/user/oauth/signup-or-in',
      {
        platform,
        source: 'browser',
        redirect_url: redirectUrl,
      }
    );
    return response.url;
  } catch (error) {
    console.error('🔐 获取OAuth认证地址失败:', error);
    throw error;
  }
};

// OAuth回调处理
export const handleOAuthCallback = async (
  code: string,
  state: string
): Promise<LoginResponse> => {
  try {
    const response = await apiGet<LoginResponse>('/v1/user/oauth/callback', {
      code,
      state,
    });

    // 保存用户信息到 sessionStorage
    // setUserInfo(response.user as unknown as Record<string, unknown>);

    return response;
  } catch (error) {
    console.error('🔐 OAuth回调处理失败:', error);
    throw error;
  }
};

// 更新用户资料
export interface UpdateProfileRequest {
  username?: string;
  avatar?: string;
  old_password?: string;
  password?: string;
}

export const updateProfile = async (
  data: UpdateProfileRequest
): Promise<UserInfo> => {
  try {
    const response = await apiPost<UserInfo>(
      '/v1/user/profile',
      data as unknown as Record<string, unknown>
    );
    return response;
  } catch (error) {
    console.error('🔐 更新用户资料失败:', error);
    throw error;
  }
};

// 获取用户权限
export const getUserPermissions = async (): Promise<UserPermissions> => {
  try {
    const response = await apiGet<UserPermissions>('/v1/tenant/me/permissions');
    console.log('🔐 获取用户权限成功:', response);
    return response;
  } catch (error) {
    console.error('🔐 获取用户权限失败:', error);
    throw error;
  }
};
